import React from "react";
import { motion } from "framer-motion";
import nextLearn from "../assets/projects/nextLearn.png";

const projects = [
  {
    title: "NextLearn — Education Platform",
    image: nextLearn,
    description:
      "A full-stack learning platform with JWT authentication, course management, and a responsive UI on a Node.js REST API backend. Built end to end — from the content hierarchy and component structure on the frontend to the data models and auth on the server.",
    tags: ["Next.js", "Node.js", "Express", "MongoDB", "JWT Auth"],
    demo: "https://next-learn-education.vercel.app/",
  },
];

const Projects = () => {
  return (
    <section name="projects" className="section-frame">
      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="section-kicker">Projects</p>
          <h2 className="section-title mt-6 max-w-3xl">
            A public project — most of my work ships inside production systems.
          </h2>
        </div>

        <p className="max-w-md text-sm leading-7 text-slate-300 sm:text-base">
          The bulk of my engineering lives in production B2B/B2C commerce — the
          payment, order, and reliability systems detailed above. Here&apos;s a
          public, end-to-end build you can explore directly.
        </p>
      </div>

      <div className="mt-16 space-y-20">
        {projects.map((project, index) => (
          <motion.article
            key={project.title}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.75, delay: index * 0.08 }}
            className="grid gap-8 lg:grid-cols-[1.05fr_0.95fr] lg:gap-12"
          >
            <a
              href={project.demo}
              target="_blank"
              rel="noreferrer"
              className={`group relative overflow-hidden rounded-[2rem] border border-white/10 ${
                index % 2 === 1 ? "lg:order-2" : ""
              }`}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-[#050b14] via-transparent to-transparent opacity-80" />
              <img
                src={project.image}
                alt={project.title}
                className="h-full min-h-[320px] w-full object-cover transition duration-700 group-hover:scale-[1.03]"
              />
            </a>

            <div
              className={`flex flex-col justify-center ${
                index % 2 === 1 ? "lg:order-1" : ""
              }`}
            >
              <h3 className="mt-4 font-['Sora'] text-3xl text-slate-50 sm:text-4xl">
                {project.title}
              </h3>
              <p className="mt-6 max-w-xl text-sm leading-8 text-slate-300 sm:text-base">
                {project.description}
              </p>

              <div className="mt-8 flex flex-wrap gap-3">
                {project.tags.map((tag) => (
                  <span
                    key={tag}
                    className="skill-chip rounded-full px-4 py-2 text-xs uppercase tracking-[0.18em]"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-10 flex flex-wrap gap-4">
                <a
                  href={project.demo}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center rounded-full bg-[#f59e0b] px-5 py-3 text-sm font-semibold uppercase tracking-[0.18em] text-slate-950 transition hover:bg-[#fbbf24]"
                >
                  Live Preview
                </a>
                {project.code ? (
                  <a
                    href={project.code}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center rounded-full border border-white/14 px-5 py-3 text-sm font-semibold uppercase tracking-[0.18em] text-slate-100 transition hover:border-cyan-300/60 hover:text-white"
                  >
                    Source Code
                  </a>
                ) : null}
              </div>
            </div>
          </motion.article>
        ))}
      </div>
    </section>
  );
};

export default Projects;
